import { SubmitHandler } from 'react-hook-form';
import { Redirect } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { useToast } from '@givebutter/primitives/toast';
import {
    associateContactsToHousehold,
    disassociateContactsFromHousehold,
    updateHouseholdById,
} from 'api/households';
import {
    SelectContactForm,
    SelectContactFormValues,
} from 'assets/js/pages/contacts/SelectContactForm';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import { useGivebutterForm } from 'components/form';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import {
    useCurrentContactHousehold,
    usePossibleCurrentContactHousehold,
} from 'customHooks/useCurrentContactHousehold';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ContactHouseholdMemberFormValues extends SelectContactFormValues {
    isPrimary: boolean;
}

const ContactHouseholdMemberFormDrawer = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { data: contact } = useCurrentContact();
    const contactId = contact.id;
    const { accountId } = useCurrentAccount();
    const { data: household } = useCurrentContactHousehold();

    const methods = useGivebutterForm<ContactHouseholdMemberFormValues>({
        defaultValues: {
            selectedContact: [],
            isPrimary: false,
        },
    });

    const {
        handleSubmit,
        reset,
        resetFormButton,
        path,
        control,
        setValue,
        setError,
        clearErrors,
        watch,
        formState: { errors },
    } = methods;

    const rawSelectedContact = watch('selectedContact');
    const selectedContact = rawSelectedContact[0] ? rawSelectedContact[0] : undefined;

    const { mutate: updatePrimary } = useMutation({
        mutationFn: (values: ContactHouseholdMemberFormValues) => {
            const contactId = values.selectedContact?.[0].value?.id;
            return updateHouseholdById(accountId, household.id, {
                primaryContactId: values.isPrimary ? contactId : undefined,
            });
        },
        onSettled: () => {
            toast.success('Added member to household successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contactId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.householdById(accountId, household.id),
            });
            goBack();
        },
    });

    const { mutate: disassociateContact, isPending: isRemovingContact } = useMutation({
        mutationFn: () => {
            const householdId = selectedContact?.value?.household_id as number;
            return disassociateContactsFromHousehold(accountId, householdId, [
                selectedContact?.value?.id!,
            ]);
        },
        onSuccess: () => {
            toast.success('Removed member from household successfully!');
            clearErrors('root');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
        },
        onError: () => {
            toast.error(`Failed to remove member from household. Please try again.`);
        },
    });

    const { mutate: submit } = useMutation({
        mutationFn: (values: ContactHouseholdMemberFormValues) => {
            const contactId = values.selectedContact?.[0].value?.id;
            return associateContactsToHousehold(
                accountId,
                household!.id,
                contactId ? [contactId] : [],
            );
        },
        onSuccess: (_, formValues) => {
            updatePrimary(formValues);
        },
        onError: (error: GivebutterAxiosError) => {
            const isAlreadyInHouseholdError = error.response?.data.message?.includes(
                'have already been associated',
            );
            if (isAlreadyInHouseholdError) {
                setError('root', { type: 'custom' });
            } else {
                toast.error(
                    error.response?.data.message ??
                        `Failed to add member to household. Please try again.`,
                );
            }

            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<ContactHouseholdMemberFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Add Household Member</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-y-4'}>
                        <Form.Container>
                            <Label htmlFor={'contact-search'} className={'text-sm font-medium'}>
                                Search for contact
                            </Label>
                            {selectedContact?.value ? (
                                <div className={'flex items-center gap-2'}>
                                    <p>
                                        {selectedContact.value.first_name}{' '}
                                        {selectedContact.value.last_name}
                                    </p>
                                    <Button
                                        variant="none"
                                        color="primary"
                                        className="font-medium"
                                        onClick={() => {
                                            setValue('selectedContact', []);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            ) : (
                                <Form.Container>
                                    <SelectContactForm />
                                </Form.Container>
                            )}
                        </Form.Container>
                        {selectedContact && (
                            <div className={'flex items-center gap-2'}>
                                <Form.UseFormCheckboxInput control={control} name={'isPrimary'} />
                                <Form.Label name={path('isPrimary')}>Primary Contact</Form.Label>
                            </div>
                        )}
                        {!!errors.root && (
                            <AlertNotification.Root type={'error'}>
                                <AlertNotification.Icon />
                                <div className={'flex flex-col gap-3'}>
                                    <AlertNotification.Content>
                                        <AlertNotification.Title>
                                            Contact is already in a household
                                        </AlertNotification.Title>
                                        <AlertNotification.Description>
                                            Remove the contact from household to continue or search
                                            again
                                        </AlertNotification.Description>
                                    </AlertNotification.Content>
                                    <div className={'flex gap-4'}>
                                        <Button
                                            variant="none"
                                            color="error"
                                            size="xs"
                                            className="font-medium"
                                            onClick={() => reset()}
                                        >
                                            New search
                                        </Button>
                                        <Button
                                            variant="none"
                                            color="error"
                                            size="xs"
                                            className="font-medium"
                                            onClick={() => disassociateContact()}
                                        >
                                            Remove from household
                                        </Button>
                                    </div>
                                </div>
                            </AlertNotification.Root>
                        )}
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton disabled={isRemovingContact}>
                                Add member to household
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const ContactHouseholdMemberFormDrawerWrapper = () => {
    const { data: contact } = useCurrentContact();
    const { accountId } = useCurrentAccount();
    const { data: household } = usePossibleCurrentContactHousehold();

    if (!household) {
        return <Redirect to={`/accounts/${accountId}/contacts/${contact.id}/household`} />;
    }

    return <ContactHouseholdMemberFormDrawer />;
};

export { ContactHouseholdMemberFormDrawerWrapper as ContactHouseholdMemberFormDrawer };
